import { useState } from "react";
import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import PageLayout from "@/components/layout/PageLayout";
import { faqs, faqGroups } from "@/mocks/faqs";

const faqSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "常见问题", item: SITE_URL + "/faq" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function Faq() {
  const [group, setGroup] = useState("全部");
  const list = group === "全部" ? faqs : faqs.filter((f) => f.group === group);

  return (
    <PageLayout>
      <Seo
        title="常见问题 - 吃瓜路线"
        description="吃瓜路线常见问题：了解什么是吃瓜路线、事件地图如何整理、信息溯源与版权原则、如何提交勘误与反馈。"
        keywords="吃瓜路线常见问题,事件整理方法,信息溯源,版权,反馈"
        path="/faq"
        schema={faqSchema}
      />

      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li aria-current="page" className="font-medium text-foreground-700">
            常见问题
          </li>
        </ol>
      </nav>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-4">
        {/* 说明栏 */}
        <aside className="lg:col-span-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">FAQ</p>
          <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950">常见问题</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            关于吃瓜路线的定位、事件地图怎么整理、信息与版权原则，以及如何参与反馈，都能在这里找到答案。
          </p>
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
              <i className="ri-mail-send-line text-accent-600" aria-hidden="true"></i>
              还是没解决？
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              想提交勘误或提建议，欢迎到
              <Link to="/about" className="mx-1 font-medium text-primary-700 hover:text-primary-800">
                关于我们
              </Link>
              页联系我们。
            </p>
          </div>
        </aside>

        {/* 问答主区 */}
        <div className="lg:col-span-3">
          {/* 分组切换 */}
          <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {["全部", ...faqGroups].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroup(g)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  group === g
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-3">
            {list.map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-background-200 bg-background-50 open:border-primary-200"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                  <span className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                      <i className="ri-question-mark text-xs" aria-hidden="true"></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-900 md:text-base">
                      {f.q}
                    </span>
                  </span>
                  <i
                    className="ri-arrow-down-s-line shrink-0 text-xl text-foreground-400 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  ></i>
                </summary>
                <div className="border-t border-background-200 px-5 py-4 md:pl-14">
                  <p className="max-w-3xl text-sm leading-relaxed text-foreground-700">{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}